import { useQuery } from '@tanstack/react-query';
import { Tenant } from '../../../schemas/tenant.schema';
import { fetchSubscribedMLS } from '../../../api/mls';
import { getStorefrontConfig } from '../../../api/storefront';

export const useTenant = (organizationId: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: ['tenant', organizationId],
    queryFn: async () => {
      if (!organizationId) throw new Error('Organization ID is required');

      // Storefront config (branding/domain/name) from the Storefront service (Spanner),
      // and detailed MLS boards from the mls service. No Firestore — that DB is gone.
      const [config, subscribedMLSRows] = await Promise.all([
        getStorefrontConfig(organizationId),
        fetchSubscribedMLS(organizationId),
      ]);


      // Same shape as before: { boardCode, enabled, agentId?, officeId? }
      const subscribedMLS = subscribedMLSRows.map((mls) => ({
        boardCode: mls.board,
        enabled: mls.enabled,
        ...(mls as Record<string, unknown>),
      }));

      // Reshape the storefront config into the Tenant shape consumers expect.
      // The full branding.logo object (url/placement/width/height) from the
      // service must win — synthesizing {url} from the flat string on top of
      // it wiped placement, which then round-tripped through save and erased
      // it in Spanner too.
      const branding = {
        ...(config.logo ? { logo: { url: config.logo } } : {}),
        ...(config.favicon ? { favicon: { url: config.favicon } } : {}),
        ...(config.branding || {}),
      };

      const tenantData = {
        id: config.id,
        name: config.name,
        // Missing (old response / error path) => assume configured, so a real
        // IDX org is never shown the create form by mistake.
        configured: config.configured ?? true,
        branding,
        domain: config.domain,
        analytics: config.analytics,
        logoUrl: config.logo,
        faviconUrl: config.favicon,
        skipRentals: config.skipRentals,
        idxConsentStyle: config.idxConsentStyle,
        // W16 read landed: the storefront returns subscribedCities as
        // [{id, name, stateCode}] from OrganizationCities.
        cities: (config.subscribedCities || []).map(
          (c: { id: string }) => c.id,
        ),
        subscribedCitiesDetails: config.subscribedCities || [],
        subscribedMLS: subscribedMLS.map((mls) => mls.boardCode),
        subscribedMLSDetails: subscribedMLS,
      } as unknown as Tenant;

      return tenantData;
    },
    enabled: enabled && !!organizationId,
  });
};
